import { IStorefrontAiGeneration } from '../../models/Storefront';

/**
 * The AI generation record for each storefront, and the small decisions that hang off it.
 *
 * Kept as plain functions rather than inside the components because three screens read the same
 * answer (the storefronts table, the settings sidebar, and the row's edit action) and the one thing
 * they must never disagree about is whether a session is still running.
 */

/** The two live states. Anything else means nothing is running on that storefront. */
export const AI_STATUS_BUILDING = 'building';
export const AI_STATUS_AWAITING_REVIEW = 'awaitingReview';

/** Storefront id -> its generation record. Rows without an id are dropped; they cannot be matched. */
export const aiGenerationsByStorefront = (
    items?: IStorefrontAiGeneration[]
): Map<string, IStorefrontAiGeneration> => {
    const map = new Map<string, IStorefrontAiGeneration>();
    for (const item of items ?? []) {
        if (!item.storefrontId) continue;
        map.set(item.storefrontId, item);
    }
    return map;
};

/**
 * The session still working on this storefront, if any.
 *
 * Both halves are required deliberately: a status with no session id cannot be resumed, and a session
 * id with no status is a finished session the API has already reconciled away.
 */
export const activeAiSessionId = (generation?: IStorefrontAiGeneration): string | undefined =>
    generation?.activeStatus && generation.activeSessionId ? generation.activeSessionId : undefined;

/** The chip to show on the row, or undefined when nothing is running. */
export const activeAiChip = (generation?: IStorefrontAiGeneration): { text: string, color: 'default' | 'warning' } | undefined => {
    switch (generation?.activeStatus) {
        case AI_STATUS_BUILDING:
            return { text: 'Building with AI', color: 'default' };
        case AI_STATUS_AWAITING_REVIEW:
            return { text: 'Ready for review', color: 'warning' };
        default:
            return undefined;
    }
};

/** The tooltip on the quiet "AI" marker a finished generated storefront keeps. */
export const aiMarkerTooltip = (generation?: IStorefrontAiGeneration): string =>
    generation?.lastModelKey
        ? `Built by the AI design assistant using ${generation.lastModelKey}.`
        : 'Built by the AI design assistant.';

/** A storefront the assistant built that nothing is currently working on. */
export const showsFinishedAiMarker = (generation?: IStorefrontAiGeneration): boolean =>
    !!generation?.generated && !activeAiSessionId(generation);

/**
 * The storefronts the settings sidebar lists.
 *
 * A store that is mid-generation is excluded: its pages are being rewritten under the user as the
 * assistant works, and half of them do not have their content yet, so a nav link into it leads
 * somewhere that looks broken. It stays visible in the storefronts table, where the row explains what
 * is happening and offers the way back into the generation. Generated-but-finished stores are
 * ordinary storefronts and are listed like any other.
 */
export const storefrontsForNav = <T extends { id?: string }>(
    storefronts: T[] | undefined,
    generations: Map<string, IStorefrontAiGeneration>
): T[] => (storefronts ?? []).filter(sf => !(sf.id && activeAiSessionId(generations.get(sf.id))));
